/**
 * Per-route document titles (HS-1221 follow-up).
 *
 * The tab is the only place a user can tell two open admin tabs apart, so each section names
 * itself there. Keys are the ones the sidebar already renders, so a section and its tab can never
 * drift apart in wording or translation.
 *
 * Matching is by longest path prefix rather than by route id: a detail or edit page belongs to its
 * section (`/institutions/42/edit` reads "Institutions"), and a new route under an existing section
 * inherits the right title without being listed here.
 */

/** Trails every section title, and stands alone where no section matches. */
export const TITLE_SUFFIX = 'SmartSash Admin';

/** What `index.html` ships, used verbatim before the app mounts and wherever nothing matches. */
export const DEFAULT_TITLE = 'SmartSash: Admin Panel';

interface TitleRoute {
  /** Path prefix, matched against `location.pathname`. */
  prefix: string;
  /** Locale key in the `app` namespace. */
  key: string;
}

/**
 * Order is irrelevant — the longest matching prefix wins — but the list is kept in the sidebar's
 * order so the two read as the same thing.
 */
const TITLE_ROUTES: readonly TitleRoute[] = [
  { prefix: '/dashboard', key: 'nav.dashboard' },
  { prefix: '/notifications', key: 'nav.notifications' },
  { prefix: '/institutions', key: 'nav.institutes' },
  { prefix: '/groups', key: 'nav.groups' },
  { prefix: '/laboratories', key: 'nav.laboratories' },
  { prefix: '/devices', key: 'nav.devices' },
  { prefix: '/installations', key: 'nav.installations' },
  // The Installer workspace (HS-1630): its dashboard owns the tracker and the stages beneath it.
  { prefix: '/installer', key: 'nav.installerDashboard' },
  { prefix: '/installer/jobs', key: 'nav.jobHistory' },
  { prefix: '/users', key: 'nav.users' },
  { prefix: '/requests', key: 'nav.requests' },
  { prefix: '/certifications', key: 'nav.certifications' },
  { prefix: '/rfid-tags', key: 'nav.rfidTags' },
  { prefix: '/streams', key: 'nav.streams' },
  { prefix: '/reports', key: 'nav.reports' },
  // The sidebar's one Reports row has two views, and both headers read "Reports" (HS-961 X8), so
  // only the tab tells History from Schedules — its key is the view's own name, not a sidebar row.
  { prefix: '/reports/schedules', key: 'reports.schedules.title' },
  { prefix: '/logs', key: 'nav.logs' },
  { prefix: '/settings', key: 'nav.settings' },
  // Public routes. They are reachable without a session, and each is a place a user can be sent
  // to by a link or by a device screen, so they name themselves too.
  { prefix: '/login', key: 'authentication.login' },
  { prefix: '/rfid-assignment', key: 'rfidAssignment.title' },
  { prefix: '/device-registration', key: 'deviceRegistration.title' },
];

/** The locale key for a pathname, or `null` when no section owns it (`/`, `/auth/callback`). */
export function pageTitleKey(pathname: string): string | null {
  let best: TitleRoute | null = null;

  for (const route of TITLE_ROUTES) {
    const isMatch = pathname === route.prefix || pathname.startsWith(`${route.prefix}/`);
    if (isMatch && (!best || route.prefix.length > best.prefix.length)) best = route;
  }

  return best?.key ?? null;
}

/** Compose the tab title from an already-translated section name. */
export function pageTitle(sectionName: string | null): string {
  return sectionName ? `${sectionName} · ${TITLE_SUFFIX}` : DEFAULT_TITLE;
}
